/**
 * EditorModeContext - Communicates visual editor mode state to layout components
 * 
 * When in editor mode (iframe visual editor), the FilterBar shows editor-specific controls
 * instead of date/filter options.
 * 
 * This context is provided at the DashboardLayout level and can be activated by EditorShell.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';

export type VariantId = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

export interface ViewportSize {
  name: string;
  width: number;
  height: number;
}

// Standard screen sizes
export const VIEWPORT_SIZES: ViewportSize[] = [
  { name: "Mobile S", width: 320, height: 568 },
  { name: "Mobile M", width: 375, height: 667 },
  { name: "Mobile L", width: 425, height: 812 },
  { name: "Tablet", width: 768, height: 1024 },
  { name: "Laptop", width: 1024, height: 768 },
  { name: "Laptop L", width: 1440, height: 900 },
  { name: "Desktop", width: 1920, height: 1080 },
  { name: "4K", width: 2560, height: 1440 },
];

interface EditorVariant {
  id: VariantId;
  name: string;
  instructionCount: number;
}

interface EditorModeState {
  isEditorMode: boolean;
  viewport: ViewportSize;
  currentVariantId: VariantId;
  variants: EditorVariant[];
  canUndo: boolean;
  canRedo: boolean;
  hasUnsavedChanges: boolean;
}

interface EditorModeActions {
  setViewport: (viewport: ViewportSize) => void;
  switchVariant: (variantId: VariantId) => void;
  createVariant: (variantId: VariantId) => void;
  undo: () => void;
  redo: () => void;
  onBack: () => void;
  onFinishEditing: () => void;
  openAISidebar: () => void;
}

interface EditorModeContextValue {
  state: EditorModeState;
  actions: EditorModeActions;
  // Method for EditorShell to activate editor mode
  activateEditorMode: (config: EditorModeConfig) => void;
  // Method for EditorShell to deactivate editor mode
  deactivateEditorMode: () => void;
  // Method for EditorShell to update state
  updateEditorState: (updates: Partial<Pick<EditorModeState, 'canUndo' | 'canRedo' | 'hasUnsavedChanges' | 'variants' | 'currentVariantId' | 'viewport'>>) => void;
}

interface EditorModeConfig {
  initialViewport?: ViewportSize;
  initialVariantId?: VariantId;
  initialVariants?: EditorVariant[];
  onViewportChange?: (viewport: ViewportSize) => void;
  onVariantSwitch?: (variantId: VariantId) => void;
  onVariantCreate?: (variantId: VariantId) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onBack?: () => void;
  onFinishEditing?: () => void;
  onOpenAISidebar?: () => void;
}

const EditorModeContext = createContext<EditorModeContextValue | null>(null);

interface EditorModeProviderProps {
  children: ReactNode;
}

/**
 * EditorModeProvider - Provides editor mode context at the layout level
 * EditorShell calls activateEditorMode() when it mounts to enable editor controls in FilterBar
 */
export function EditorModeProvider({ children }: EditorModeProviderProps) {
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [viewport, setViewportState] = useState<ViewportSize>(VIEWPORT_SIZES[6]);
  const [currentVariantId, setCurrentVariantId] = useState<VariantId>('A');
  const [variants, setVariants] = useState<EditorVariant[]>([{ id: 'A', name: 'Control', instructionCount: 0 }]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef<EditorModeConfig>({});

  const activateEditorMode = useCallback((config: EditorModeConfig) => {
    setIsEditorMode(true);
    if (config.initialViewport) setViewportState(config.initialViewport);
    if (config.initialVariantId) setCurrentVariantId(config.initialVariantId);
    if (config.initialVariants) setVariants(config.initialVariants);
    callbacksRef.current = config;
  }, []);

  const deactivateEditorMode = useCallback(() => {
    setIsEditorMode(false);
    callbacksRef.current = {};
  }, []);

  const updateEditorState = useCallback((updates: Partial<Pick<EditorModeState, 'canUndo' | 'canRedo' | 'hasUnsavedChanges' | 'variants' | 'currentVariantId' | 'viewport'>>) => {
    if (updates.canUndo !== undefined) setCanUndo(updates.canUndo);
    if (updates.canRedo !== undefined) setCanRedo(updates.canRedo);
    if (updates.hasUnsavedChanges !== undefined) setHasUnsavedChanges(updates.hasUnsavedChanges);
    if (updates.variants !== undefined) setVariants(updates.variants);
    if (updates.currentVariantId !== undefined) setCurrentVariantId(updates.currentVariantId);
    if (updates.viewport !== undefined) setViewportState(updates.viewport);
  }, []);

  const setViewport = useCallback((newViewport: ViewportSize) => {
    setViewportState(newViewport);
    callbacksRef.current.onViewportChange?.(newViewport);
  }, []);

  const switchVariant = useCallback((variantId: VariantId) => {
    setCurrentVariantId(variantId);
    callbacksRef.current.onVariantSwitch?.(variantId);
  }, []);

  const createVariant = useCallback((variantId: VariantId) => {
    const newVariant: EditorVariant = {
      id: variantId,
      name: `Variant ${variantId}`,
      instructionCount: 0,
    };
    setVariants(prev => [...prev, newVariant]);
    callbacksRef.current.onVariantCreate?.(variantId);
  }, []);

  const undo = useCallback(() => {
    callbacksRef.current.onUndo?.();
  }, []);

  const redo = useCallback(() => {
    callbacksRef.current.onRedo?.();
  }, []);

  const handleBack = useCallback(() => {
    callbacksRef.current.onBack?.();
  }, []);

  const handleFinishEditing = useCallback(() => {
    callbacksRef.current.onFinishEditing?.();
  }, []);

  const openAISidebar = useCallback(() => {
    callbacksRef.current.onOpenAISidebar?.();
  }, []);

  const value: EditorModeContextValue = {
    state: {
      isEditorMode,
      viewport,
      currentVariantId,
      variants,
      canUndo,
      canRedo,
      hasUnsavedChanges,
    },
    actions: {
      setViewport,
      switchVariant,
      createVariant,
      undo,
      redo,
      onBack: handleBack,
      onFinishEditing: handleFinishEditing,
      openAISidebar,
    },
    activateEditorMode,
    deactivateEditorMode,
    updateEditorState,
  };

  return (
    <EditorModeContext.Provider value={value}>
      {children}
    </EditorModeContext.Provider>
  );
}

function useEditorMode() {
  const context = useContext(EditorModeContext);
  if (!context) {
    throw new Error('useEditorMode must be used within EditorModeProvider');
  }
  return context;
}

export function useEditorModeSafe() {
  return useContext(EditorModeContext);
}

